import React, { forwardRef, useState, ChangeEvent, FocusEvent, ReactNode } from 'react';
import InputMask, { Props as InputMaskProps, ReactInputMask } from 'react-input-mask';
import { twMerge } from '@shared/utils/twMerge';
import styles from './InputNumber.module.scss';
import { ControlProps } from '@jsonforms/core';
import { convertRegExpToString } from './utils/convertRegExpToString';
import { formatInputValue } from './utils/inputFormatting';

export type MaskedInputNumberProps = Omit<InputMaskProps, 'mask'> & {
  label?: string;
  errors?: string;
  inOneLine?: boolean;
  classNameInputWrapper?: string;
  classNameLabel?: string;
  extraHelper?: ReactNode;
  maskChar?: string;
  onChange?: (value: string) => void;
  schema?: ControlProps['schema'];
};

const inputWrapperDefault = 'max-w-[41.2rem] grow';

export const MaskedInputNumber = forwardRef<ReactInputMask, MaskedInputNumberProps>(
  ({ inOneLine, required, extraHelper, maskChar = '_', onChange, schema, ...props }, ref) => {
    const [inputValue, setInputValue] = useState('');
    const maskPattern = schema?.pattern ? convertRegExpToString(schema.pattern) : '';

    const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setInputValue(value);

      if (onChange) {
        onChange(value);
      }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      const finalValue = formatInputValue(inputValue, maskPattern, maskChar);
      setInputValue(finalValue);
      if (onChange) {
        onChange(finalValue);
      }
    };

    return (
      <div
        className="w-full"
        data-testid={`${props.name}-input-container`}
      >
        <label
          className={twMerge(
            'flex w-full flex-col',
            inOneLine ? 'flex-row justify-between' : '',
            props.classNameLabel || ''
          )}
        >
          <div className={twMerge('flex items-center', inOneLine ? 'pt-[1.2rem]' : '')}>
            {props.label && (
              <span className={twMerge('text-1424 font-semibold', required && 'field-required')}>
                {props.label}
              </span>
            )}
            {extraHelper}
          </div>
          <div className={twMerge(inputWrapperDefault, props.classNameInputWrapper || '')}>
            {React.createElement(InputMask as any, {
              ...props,
              ref,
              mask: maskPattern,
              maskChar,
              placeholder: props.placeholder ?? props.label,
              value: inputValue,
              onChange: handleChange,
              onBlur: handleBlur,
              className: twMerge(
                'ant-input ant-input-number w-full',
                styles.input,
                props.errors && 'ant-input-number-error',
                props.className || ''
              )
            })}
            {props.errors && (
              <span
                data-testid={`${props.name}-error-container`}
                className={twMerge('ant-input-error text-1420 font-normal', props.className || '')}
              >
                {props.errors}
              </span>
            )}
          </div>
        </label>
      </div>
    );
  }
);

MaskedInputNumber.displayName = 'MaskedInputNumber';
